import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import AdventureCard from "../components/AdventureCard";
import { TopBar } from "../components/Navigation";
import { Search, Map as MapIcon, X } from "lucide-react";
import MapView from "../components/MapView";
import { ACTIVITY_CATEGORIES, ALL_ACTIVITIES, categorySlugs, searchActivities } from "../lib/interests";

// "All" + the 13 shared activity categories (single source of truth).
const CATEGORY_CHIPS = [
    { id: "all", label: "All" },
    ...ACTIVITY_CATEGORIES.map((c) => ({ id: c.key, label: c.label })),
];

export default function Explore() {
    const [catKey, setCatKey] = useState("all");   // selected category chip
    const [picked, setPicked] = useState(null);      // a specific {slug,label,category} via search
    const [actQ, setActQ] = useState("");            // activity typeahead query
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [showMap, setShowMap] = useState(false);
    const [loading, setLoading] = useState(true);
    const actBoxRef = useRef(null);

    // The `activity` param sent to the API: a specific slug, a CSV of a
    // category's sub-slugs, or "all".
    const activityParam = useMemo(() => {
        if (picked) return picked.slug;
        if (catKey !== "all") return categorySlugs(catKey).join(",") || "all";
        return "all";
    }, [picked, catKey]);

    const actMatches = useMemo(() => (actQ.trim() ? searchActivities(actQ, 8) : []), [actQ]);

    // Close the typeahead dropdown on outside click.
    useEffect(() => {
        const onDoc = (e) => {
            if (actBoxRef.current && !actBoxRef.current.contains(e.target)) setActQ("");
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    useEffect(() => {
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/adventures", { params: { activity: activityParam, q: q || undefined } });
                setItems(data);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(t);
    }, [activityParam, q]);

    const toggleLike = async (adv) => {
        try {
            const { data } = await api.post(`/adventures/${adv.id}/like`);
            setItems((xs) => xs.map((x) => (x.id === adv.id ? { ...x, liked: data.liked, likes_count: x.likes_count + (data.liked ? 1 : -1) } : x)));
        } catch {}
    };
    const toggleSave = async (adv) => {
        try {
            const { data } = await api.post(`/adventures/${adv.id}/save`);
            setItems((xs) => xs.map((x) => (x.id === adv.id ? { ...x, saved: data.saved, saves_count: x.saves_count + (data.saved ? 1 : -1) } : x)));
        } catch {}
    };

    const markers = items.filter((i) => i.lat && i.lng).map((i) => ({ position: [i.lat, i.lng], label: i.title }));
    const mapCenter = markers[0]?.position;

    return (
        <div>
            <TopBar title="Explore" subtitle="Discover real adventures" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="hidden lg:block mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-2">Explore</div>
                    <h1 className="font-display font-black text-4xl lg:text-5xl text-midnight-pine tracking-tight">
                        Find your next trail
                    </h1>
                </div>

                {/* Search + toggle */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                        <input
                            data-testid="explore-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by title, location, description…"
                            className="w-full pl-10 pr-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                        />
                    </div>
                    <button
                        onClick={() => setShowMap(!showMap)}
                        data-testid="explore-toggle-map"
                        className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md font-bold text-sm transition-all ${
                            showMap
                                ? "bg-midnight-pine text-white"
                                : "bg-snowcap border border-ridge-line text-granite hover:border-midnight-pine"
                        }`}
                    >
                        <MapIcon className="w-4 h-4" /> {showMap ? "Hide map" : "Show map"}
                    </button>
                </div>

                {/* Activity search (specific sub-activity) */}
                <div ref={actBoxRef} className="relative mb-4 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                    <input
                        data-testid="explore-activity-search"
                        value={actQ}
                        onChange={(e) => setActQ(e.target.value)}
                        placeholder="Find a specific activity — e.g. fly fishing, snowshoeing…"
                        className="w-full pl-10 pr-4 py-2.5 rounded-md bg-snowcap border border-ridge-line text-sm focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                    />
                    {actMatches.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full bg-white border border-ridge-line rounded-lg shadow-lg overflow-hidden">
                            {actMatches.map((a) => (
                                <button
                                    key={a.slug}
                                    data-testid={`explore-activity-opt-${a.slug}`}
                                    onClick={() => { setPicked(a); setCatKey("all"); setActQ(""); }}
                                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-alpine-mist"
                                >
                                    <span className="font-semibold text-granite">{a.label}</span>
                                    <span className="text-xs text-slate-gray">{a.category}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Selected specific-activity pill */}
                {picked && (
                    <div className="mb-4">
                        <span
                            data-testid="explore-activity-pill"
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ember-orange text-white text-sm font-semibold"
                        >
                            {picked.label}
                            <button data-testid="explore-activity-pill-clear" onClick={() => setPicked(null)} aria-label="Clear activity">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </span>
                    </div>
                )}

                {/* Category chips */}
                <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-4 px-4 lg:mx-0 lg:px-0">
                    {CATEGORY_CHIPS.map((a) => {
                        const active = !picked && a.id === catKey;
                        return (
                            <button
                                key={a.id}
                                data-testid={`explore-activity-${a.id}`}
                                onClick={() => { setCatKey(a.id); setPicked(null); }}
                                className={`px-4 py-2 rounded-full text-sm font-semibold shrink-0 transition-all ${
                                    active
                                        ? "bg-ember-orange text-white"
                                        : "bg-snowcap text-granite border border-ridge-line hover:border-ember-orange"
                                }`}
                            >
                                {a.label}
                            </button>
                        );
                    })}
                </div>

                {/* Map */}
                {showMap && (
                    <div className="h-[400px] mb-6 rounded-lg overflow-hidden border border-ridge-line">
                        <MapView center={mapCenter} zoom={5} markers={markers} />
                    </div>
                )}

                {/* Grid */}
                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                                <div className="aspect-[16/10] bg-trail-tan/50 animate-pulse" />
                                <div className="p-5 space-y-2">
                                    <div className="h-4 bg-ridge-line rounded animate-pulse w-3/4" />
                                    <div className="h-3 bg-ridge-line rounded animate-pulse w-1/2" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="bg-snowcap border border-ridge-line rounded-lg p-12 text-center">
                        <h3 className="font-display font-bold text-xl text-midnight-pine mb-2">No matches</h3>
                        <p className="text-slate-gray">Try a different activity or search term.</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {items.map((adv) => (
                            <AdventureCard key={adv.id} adventure={adv} onLike={toggleLike} onSave={toggleSave} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
